import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    LayoutDashboard, CalendarCheck, Package, FileSignature,
    HardHat, AlertOctagon, Image as ImageIcon2, Camera,
    Plus, X, CheckCircle2, Box, MapPin,
    Hammer, Search, AlertCircle, ChevronRight,
    RefreshCw, Layers, AlertTriangle, Receipt, IndianRupee, CheckCircle2 as CheckIcon,
    Paperclip, FileText
} from 'lucide-react';
import { api, API_BASE, getAuthToken } from '../api';
import { MATERIAL_CATEGORIES, MATERIALS } from '../materials';
import { useServerEvents } from '../useServerEvents';
import TasksTree from './TasksTree';
import LabourVendorEntry from './LabourVendorEntry';


const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  :root {
    --bg: #f0f4f8; --card: #ffffff; --border: #e2e8f0;
    --accent: #1743e6; --alight: #eef3ff;
    --muted: #64748b; --main: #0f172a;
    --sh: 0 1px 3px rgba(0,0,0,0.06);
    --shd: 0 4px 14px rgba(0,0,0,0.08);
    --shlg: 0 12px 32px rgba(0,0,0,0.14);
  }
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; font-family: 'Plus Jakarta Sans', sans-serif; background: var(--bg); color: var(--main); -webkit-font-smoothing: antialiased; }

  .ef-root { display: flex; height: 100%; overflow: hidden; }

  /* Sidebar */
  .ef-sb { width: 230px; background: linear-gradient(180deg, #1130c2 0%, #1743e6 60%, #1c4bf2 100%); flex-shrink: 0; display: flex; flex-direction: column; padding: 18px 12px; overflow-y: auto; z-index: 10; }
  .ef-sb::-webkit-scrollbar { display: none; }
  .ef-logo { display: flex; align-items: center; gap: 9px; padding: 0 6px; margin-bottom: 6px; }
  .ef-logo-icon { width: 30px; height: 30px; border-radius: 8px; background: linear-gradient(135deg,#1743e6,#2f5bf0); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .ef-logo-txt { font-size: 12px; font-weight: 800; color: #fff; letter-spacing: 0.5px; text-transform: uppercase; line-height: 1.2; }
  .ef-pill { margin: 14px 0 8px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.09); border-radius: 9px; padding: 12px; }
  .ef-pill .lbl { font-size: 9px; font-weight: 700; color: rgba(255,255,255,0.75); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 3px; }
  .ef-pill .nm { font-size: 13px; font-weight: 800; color: #fff; }
  .ef-pill .loc { display: flex; align-items: center; gap: 3px; font-size: 10px; color: rgba(255,255,255,0.35); margin-top: 3px; }
  .ef-nav { flex: 1; display: flex; flex-direction: column; gap: 2px; margin-top: 6px; }
  .ef-ni { display: flex; align-items: center; gap: 9px; padding: 9px 10px; border-radius: 7px; border: none; background: transparent; color: rgba(255,255,255,0.75); cursor: pointer; font-size: 12px; font-weight: 500; font-family: 'Plus Jakarta Sans',sans-serif; transition: all 0.12s; width: 100%; text-align: left; }
  .ef-ni:hover { background: rgba(255,255,255,0.10); color: #fff; }
  .ef-ni.active { background: rgba(255,255,255,0.18); color: #fff; font-weight: 700; border-left: 2px solid #ffffff; padding-left: 8px; }
  .ef-ni .dot { width: 5px; height: 5px; border-radius: 50%; background: #ef4444; margin-left: auto; flex-shrink: 0; animation: pulse 1.5s infinite; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  .ef-user { margin-top: auto; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.07); display: flex; align-items: center; gap: 9px; }
  .ef-av { width: 32px; height: 32px; border-radius: 9px; background: linear-gradient(135deg,#1743e6,#1c4bf2); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; color: #fff; flex-shrink: 0; }
  .ef-uname { font-size: 12px; font-weight: 700; color: #fff; line-height: 1.2; }
  .ef-urole { font-size: 10px; color: rgba(255,255,255,0.30); }

  /* Main */
  .ef-main { flex: 1; overflow-y: auto; padding: 26px; background: var(--bg); }
  .ef-main::-webkit-scrollbar { width: 4px; }
  .ef-main::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
  .ef-hdr { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
  .ef-ttl { font-size: 20px; font-weight: 800; margin: 0; letter-spacing: -0.3px; }
  .ef-sub { font-size: 12px; color: var(--muted); margin: 2px 0 0; }
  .ef-hr { display: flex; gap: 8px; align-items: center; }

  /* Cards */
  .card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 18px; box-shadow: var(--sh); transition: box-shadow 0.15s; }
  .card:hover { box-shadow: var(--shd); }
  .acard { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; cursor: pointer; transition: all 0.16s; display: flex; align-items: center; gap: 12px; box-shadow: var(--sh); }
  .acard:hover { transform: translateY(-2px); box-shadow: var(--shd); border-color: var(--accent); }
  .acard-ico { padding: 9px; border-radius: 9px; flex-shrink: 0; }

  /* Stat strip */
  .stat-strip { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin-bottom: 18px; }
  .sc { background: var(--card); border: 1px solid var(--border); border-radius: 10px; padding: 14px; box-shadow: var(--sh); }
  .sc-lbl { font-size: 9px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 5px; }
  .sc-val { font-size: 24px; font-weight: 800; line-height: 1; }
  .sc-sub { font-size: 10px; color: var(--muted); margin-top: 3px; }

  /* Table */
  .tbl { width: 100%; border-collapse: collapse; }
  .tbl th { text-align: left; padding: 9px 10px; color: var(--muted); font-size: 10px; font-weight: 700; border-bottom: 1px solid var(--border); text-transform: uppercase; letter-spacing: 0.5px; background: #f8fafc; }
  .tbl th:first-child { border-radius: 6px 0 0 6px; }
  .tbl th:last-child { border-radius: 0 6px 6px 0; }
  .tbl td { padding: 12px 10px; border-bottom: 1px solid #f8fafc; font-size: 13px; color: #334155; }
  .tbl tr:last-child td { border-bottom: none; }
  .tbl tr.cr { cursor: pointer; transition: background 0.1s; }
  .tbl tr.cr:hover td { background: #f8fafc; }

  /* Progress */
  .pb { height: 5px; background: #f1f5f9; border-radius: 3px; overflow: hidden; margin-top: 4px; }
  .pf { height: 100%; border-radius: 3px; transition: width 0.5s ease; }

  /* Badge */
  .badge { padding: 3px 7px; border-radius: 4px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; display: inline-flex; align-items: center; gap: 2px; }
  .badge-completed,.badge-approved,.badge-resolved,.badge-reviewed,.badge-submitted,.badge-on-track { background: #d1fae5; color: #065f46; }
  .badge-delayed,.badge-rejected,.badge-high,.badge-open { background: #fee2e2; color: #991b1b; }
  .badge-in-progress,.badge-pending,.badge-medium,.badge-at-risk { background: #fef3c7; color: #92400e; }
  .badge-planned,.badge-low,.badge-planning { background: #eef3ff; color: #1743e6; }
  .badge-out-for-delivery { background: #e0e7ff; color: #0f2ea8; }

  /* Buttons */
  .btn { display: inline-flex; align-items: center; gap: 5px; padding: 7px 14px; border-radius: 7px; font-weight: 600; cursor: pointer; font-size: 12px; font-family: 'Plus Jakarta Sans',sans-serif; border: none; transition: all 0.12s; justify-content: center; }
  .btn-primary { background: var(--accent); color: #fff; }
  .btn-primary:hover { background: #0f2ea8; }
  .btn-ok { background: #10b981; color: #fff; }
  .btn-ok:hover { background: #059669; }
  .btn-warn { background: #f59e0b; color: #fff; }
  .btn-warn:hover { background: #d97706; }
  .btn-danger { background: #ef4444; color: #fff; }
  .btn-danger:hover { background: #dc2626; }
  .btn-ghost { background: #fff; border: 1px solid var(--border); color: var(--main); }

  /* Modal */
  .moverlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 1000; backdrop-filter: blur(5px); animation: fadeIn 0.16s; }
  .mbox { background: #fff; width: 100%; max-width: 540px; padding: 0; border-radius: 16px; box-shadow: var(--shlg); max-height: 92vh; overflow-y: auto; animation: slideUp 0.2s; border: 1px solid var(--border); }
  .mhdr { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px 16px; border-bottom: 1px solid #f1f5f9; }
  .mhdr h2 { margin: 0; font-size: 15px; font-weight: 800; }
  .mbody { padding: 20px 24px 24px; }
  .mcls { background: #f1f5f9; border: none; cursor: pointer; color: var(--muted); padding: 5px; border-radius: 7px; display: flex; align-items: center; }
  .mcls:hover { background: #e2e8f0; color: var(--main); }

  /* Forms */
  .fg { margin-bottom: 13px; }
  .fg label { display: block; font-size: 11px; font-weight: 700; margin-bottom: 4px; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; }
  .fg input, .fg select, .fg textarea { width: 100%; padding: 9px 11px; border: 1.5px solid var(--border); border-radius: 7px; font-size: 13px; outline: none; transition: border-color 0.12s, box-shadow 0.12s; font-family: 'Plus Jakarta Sans',sans-serif; background: #fff; color: var(--main); }
  .fg input:focus, .fg select:focus, .fg textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px #eef3ff; }
  .fg textarea { resize: vertical; min-height: 80px; line-height: 1.5; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }

  /* Banners */
  .abanner { display: flex; align-items: center; gap: 9px; padding: 10px 14px; border-radius: 9px; margin-bottom: 12px; font-size: 12px; font-weight: 600; }
  .abanner.danger { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
  .abanner.warn   { background: #fffbeb; border: 1px solid #fde68a; color: #b45309; }

  /* Gallery */
  .gal-grid { display: grid; grid-template-columns: repeat(auto-fill,minmax(220px,1fr)); gap: 12px; }
  .gal-card { border-radius: 10px; overflow: hidden; border: 1px solid var(--border); background: var(--card); transition: transform 0.15s, box-shadow 0.15s; }
  .gal-card:hover { transform: translateY(-3px); box-shadow: var(--shd); }
  .gal-img { width: 100%; height: 150px; object-fit: cover; background: #f1f5f9; display: block; }

  /* Error state */
  .err-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; text-align: center; gap: 12px; }

  /* Misc */
  .loading { display: flex; align-items: center; justify-content: center; padding: 60px; gap: 10px; color: var(--muted); font-size: 13px; }
  input[type=range] { -webkit-appearance: none; width: 100%; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
  input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 17px; height: 17px; border-radius: 50%; background: var(--accent); box-shadow: 0 2px 6px rgba(29,78,216,0.35); }

  @keyframes fadeIn  { from { opacity: 0; }                          to { opacity: 1; } }
  @keyframes slideUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes spin    { to { transform: rotate(360deg); } }
  .spinning { animation: spin 0.8s linear infinite; }
  @keyframes sIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sg > * { animation: sIn 0.26s ease both; }
  .sg > *:nth-child(1){animation-delay:.03s} .sg > *:nth-child(2){animation-delay:.07s}
  .sg > *:nth-child(3){animation-delay:.11s} .sg > *:nth-child(4){animation-delay:.15s}
  .sg > *:nth-child(5){animation-delay:.19s} .sg > *:nth-child(6){animation-delay:.23s}
`;

export default function EngineerDashboard({ user }) {
    const [tab, setTab] = useState('Field Dashboard');
    const [labourTrades, setLabourTrades] = useState([]);
    const [labourDate, setLabourDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [labourCounts, setLabourCounts] = useState({});
    const [labourOT, setLabourOT] = useState({});   // trade_name -> { w, h }
    const [labourSaving, setLabourSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [showExpModal, setShowExpModal] = useState(false);
    const [expFile, setExpFile] = useState(null);
    const [expPickedUrl, setExpPickedUrl] = useState('');
    const [expUploading, setExpUploading] = useState(false);
    const [showInvModal, setShowInvModal] = useState(false);
    const [invFile, setInvFile] = useState(null);
    const [invPickedUrl, setInvPickedUrl] = useState('');
    const [invUploading, setInvUploading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    useEffect(() => { (async () => { try { setInvoices((await api('finance/invoices')) || []); } catch (e) {} })(); }, []);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);
    const [search, setSearch] = useState('');
    const [rangeVal, setRangeVal] = useState(0);

    const [projects, setProjects] = useState([]);
    const [engineers, setEngineers] = useState([]);
    const [indents, setIndents] = useState([]);
    const [dprs, setDprs] = useState([]);
    const [issues, setIssues] = useState([]);
    const [laborLogs, setLaborLogs] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [inventory, setInventory] = useState([]);

    const [modals, setModals] = useState({ task: false, indent: false, dpr: false, issue: false, labor: false });
    const openM = k => setModals(m => ({ ...m, [k]: true }));
    const closeM = k => setModals(m => ({ ...m, [k]: false }));
    const [selectedTask, setSelectedTask] = useState(null);

    const [indentCat, setIndentCat] = useState('');
    const [indentBrand, setIndentBrand] = useState('');
    const [indentProduct, setIndentProduct] = useState('');

    const showToast = useCallback((msg) => {
        setToast(msg); setTimeout(() => setToast(null), 3500);
    }, []);

    const [liveStatus, setLiveStatus] = useState(navigator.onLine ? 'connecting' : 'offline');
    const lastOkRef = useRef(0);
    const modalOpenRef = useRef(false);   // true while a modal/form is open — pauses live refresh
    const [lightbox, setLightbox] = useState(null);

    const applyPartial = useCallback((data) => {
        if (modalOpenRef.current) return;   // don't disturb an open form mid-typing
        if (data.projects) setProjects(Array.isArray(data.projects) ? data.projects : []);
        if (data.engineers) setEngineers(Array.isArray(data.engineers) ? data.engineers : []);
        if (data.indents) setIndents(Array.isArray(data.indents) ? data.indents : []);
        if (data.dprs) setDprs(Array.isArray(data.dprs) ? data.dprs : []);
        if (data.issues) setIssues(Array.isArray(data.issues) ? data.issues : []);
        if (data.labor) setLaborLogs(Array.isArray(data.labor) ? data.labor : []);
        if (data.gallery) setGallery(Array.isArray(data.gallery) ? data.gallery : []);
        if (data.inventory) setInventory(Array.isArray(data.inventory) ? data.inventory : []);
        if (data.expenses) setExpenses(Array.isArray(data.expenses) ? data.expenses : []);
    }, []);


    const engineerName = user?.name || '';
    // The engineer's OWN uploaded photos — usable as a bill/receipt without re-uploading.
    const myPhotos = (gallery || []).filter(g => g.uploader === engineerName && (g.img || g.url));
    const initials = engineerName.split(' ').map(n => n[0]).join('').slice(0, 2);

    // ── Fetch ──────────────────────────────────────────────────
    const applyData = useCallback((p, eng, i, d, is, l, g, inv) => {
        setProjects(Array.isArray(p) ? p : []);
        setEngineers(Array.isArray(eng) ? eng : []);
        setIndents(Array.isArray(i) ? i : []);
        setDprs(Array.isArray(d) ? d : []);
        setIssues(Array.isArray(is) ? is : []);
        setLaborLogs(Array.isArray(l) ? l : []);
        setGallery(Array.isArray(g) ? g : []);
        setInventory(Array.isArray(inv) ? inv : []);
    }, []);

    const fetchAll = useCallback(async () => {
        if (modalOpenRef.current) return;   // pause the live refresh while a form is open
        setLoading(true);
        setError('');
        try {
            const [p, eng, i, d, is, l, g, inv, exp] = await Promise.all([
                api('projects'), api('engineers'),
                api('indents'), api('dprs'),
                api('issues'), api('labor'),
                api('gallery'), api('inventory'),
                api('finance/expenses'),
            ]);
            applyData(p, eng, i, d, is, l, g, inv);
            setExpenses(Array.isArray(exp) ? exp : []);
            lastOkRef.current = Date.now();
            setLiveStatus('live');
        } catch (err) {
            setLiveStatus('offline');
            setError(err.message || 'Failed to load data. Check your connection.');
        } finally {
            setLoading(false);
        }
    }, [applyData]);

    // Always load on mount — SSE is enhancement only
    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Safety timeout — if SSE doesn't connect in 6s, load via HTTP
    useEffect(() => {
        const t = setTimeout(() => { if (loading) fetchAll(); }, 6000);
        return () => clearTimeout(t);
    }, []); // eslint-disable-line

    useEffect(() => {
        const off = () => setLiveStatus('offline');
        const on = () => { setLiveStatus('connecting'); fetchAll(); };
        window.addEventListener('offline', off); window.addEventListener('online', on);
        const hb = setInterval(async () => {
            if (!navigator.onLine) { setLiveStatus('offline'); return; }
            try {
                const c = new AbortController(); const t = setTimeout(() => c.abort(), 4000);
                const r = await fetch(`${API_BASE}/health`, { signal: c.signal, cache: 'no-store' });
                clearTimeout(t);
                if (r.ok) { lastOkRef.current = Date.now(); setLiveStatus('live'); }
            } catch { if (Date.now() - lastOkRef.current > 8000) setLiveStatus('offline'); }
        }, 5000);
        return () => { window.removeEventListener('offline', off); window.removeEventListener('online', on); clearInterval(hb); };
    }, [fetchAll]);

    // SSE — placed AFTER fetchAll so it can reference it
    useServerEvents({
        onConnected: useCallback(() => setLiveStatus('live'), []),
        onError: useCallback(() => {
            fetchAll(); // SSE optional — let HTTP decide connectivity
        }, [fetchAll]),
        onInit: useCallback((data) => {
            applyPartial(data);
            setLoading(false);
            setLiveStatus('live');
        }, [applyPartial]),
        onUpdate: useCallback((data) => {
            applyPartial(data);
        }, [applyPartial]),
    });

    // Pause the live refresh whenever the invoice or expense modal is open,
    // so a background update never wipes what the engineer is typing.
    useEffect(() => {
        const open = showExpModal || showInvModal;
        const was = modalOpenRef.current;
        modalOpenRef.current = open;
        if (was && !open) { const t = setTimeout(() => fetchAll(), 150); return () => clearTimeout(t); }
    }, [showExpModal, showInvModal]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Derived — scoped to this engineer ─────────────────────
    // Find this engineer's record to get their assigned site
    const myEngineer = engineers.find(e => e.name === engineerName || e.email === user?.email);
    const mySiteName = myEngineer?.site || user?.engineer_site || (projects[0]?.name ?? '');
    const mySite = projects.find(p => p.name === mySiteName) || projects[0] || null;

    // ── Labour Attendance (counts per trade for this engineer's site) ──
    useEffect(() => {
        (async () => { try { const t = await api('labour_trades'); setLabourTrades(Array.isArray(t) ? t : []); } catch { /* ignore */ } })();
    }, []);
    useEffect(() => {
        if (tab !== 'Labour Attendance' || !mySiteName) return;
        (async () => {
            try {
                const rows = await api(`labour_attendance?date=${labourDate}&site=${encodeURIComponent(mySiteName)}`);
                const map = {}, ot = {}; (rows || []).forEach(r => { map[r.trade_name] = Number(r.count) || 0; ot[r.trade_name] = { w: Number(r.ot_workers) || 0, h: Number(r.ot_hours) || 0 }; });
                setLabourOT(ot);
                setLabourCounts(map);
            } catch { setLabourCounts({}); }
        })();
    }, [tab, labourDate, mySiteName]); // eslint-disable-line
    const setTradeCount = (trade, val) => {
        const n = Math.max(0, parseInt(val, 10) || 0);
        setLabourCounts(c => ({ ...c, [trade]: n }));
    };
    const setTradeOT = (trade, field, val) => {
        const n = Math.max(0, field === 'h' ? (parseFloat(val) || 0) : (parseInt(val, 10) || 0));
        setLabourOT(o => ({ ...o, [trade]: { ...(o[trade] || { w: 0, h: 0 }), [field]: n } }));
    };
    const labourTotal = labourTrades.reduce((s, t) => s + (Number(labourCounts[t.name]) || 0), 0);
    const saveLabourAttendance = async () => {
        if (!mySiteName) { showToast('No site assigned to you yet.'); return; }
        setLabourSaving(true);
        try {
            const entries = labourTrades.map(t => ({ trade_id: t.id, trade_name: t.name, category: t.category, count: Number(labourCounts[t.name]) || 0, ot_workers: labourOT[t.name]?.w || 0, ot_hours: labourOT[t.name]?.h || 0 }));
            await api('labour_attendance', 'POST', { date: labourDate, site: mySiteName, marked_by: engineerName, entries });
            showToast('Labour attendance saved.');
        } catch { showToast('Error saving attendance.'); }
        finally { setLabourSaving(false); }
    };

    // Show only this engineer's indents (by name in requested_by / requestedBy)
    const myIndents = indents.filter(i =>
        (i.requested_by === engineerName || i.requestedBy === engineerName) ||
        (mySite && i.project === mySite.name)
    );
    const myDprs = dprs.filter(d => d.author === engineerName);
    const myIssues = issues.filter(i => i.assigned_to === engineerName || i.assignedTo === engineerName);
    const openIssues = myIssues.filter(i => i.status === 'Open').length;
    const pendingInds = myIndents.filter(i => i.status === 'Pending').length;

    const allItems = inventory.flatMap(s => s.items || []);
    const critItems = allItems.filter(it => it.status === 'Critical' || it.status === 'Low Stock');

    const allTasks = projects.flatMap(p => (p.tasks || []).map(t => ({ ...t, projectName: p.name, projectId: p.id })));

    // ── Actions ───────────────────────────────────────────────
    const doUpdateTask = async (e) => {
        e.preventDefault();
        const progress = rangeVal;
        const status = progress === 100 ? 'Completed' : progress > 0 ? 'In Progress' : 'Planned';
        const proj = projects.find(p => (p.tasks || []).some(t => t.id === selectedTask.id));
        if (!proj) { showToast('Project not found.'); return; }
        const upd = (proj.tasks || []).map(t =>
            t.id === selectedTask.id ? { ...t, progress, status } : t
        );
        try {
            await api(`projects/${proj.id}`, 'PUT', {
                tasks: upd,
                progress: Math.round(upd.reduce((a, t) => a + t.progress, 0) / upd.length),
            });
            const p = await api('projects');
            setProjects(Array.isArray(p) ? p : []);
            closeM('task');
            showToast(`"${selectedTask.name}" updated to ${progress}%`);
        } catch { showToast('Error saving task. Please try again.'); }
    };

    const doRaiseIndent = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        const item = indentCat === 'Other'
            ? f.get('custom_item')
            : indentBrand || indentCat;
        if (!item) { showToast('Please select a material.'); return; }
        try {
            await api('indents', 'POST', {
                project: mySite?.name || '',
                priority: f.get('priority'),
                item,
                qty: f.get('qty'),
                uom: f.get('uom'),
                requestedBy: engineerName,
                requested_by: engineerName,
                vendor: 'TBD',
            });
            const i = await api('indents');
            setIndents(Array.isArray(i) ? i : []);
            closeM('indent');
            setIndentCat(''); setIndentBrand(''); setIndentProduct('');
            showToast('Indent submitted — awaiting admin approval.');
        } catch { showToast('Failed to submit indent. Please try again.'); }
    };

    const doSubmitDPR = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        try {
            await api('dprs', 'POST', {
                site: mySite?.name || '',
                author: engineerName,
                summary: f.get('summary'),
            });
            const d = await api('dprs');
            setDprs(Array.isArray(d) ? d : []);
            closeM('dpr');
            showToast('DPR submitted successfully.');
        } catch { showToast('Failed to submit DPR. Please try again.'); }
    };

    const doLogIssue = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        try {
            await api('issues', 'POST', {
                site: mySite?.name || '',
                title: f.get('title'),
                severity: f.get('severity'),
                assignedTo: engineerName,
                assigned_to: engineerName,
            });
            const is = await api('issues');
            setIssues(Array.isArray(is) ? is : []);
            closeM('issue');
            showToast('Issue reported to admin.');
        } catch { showToast('Failed to report issue. Please try again.'); }
    };

    const doLogLabor = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        try {
            await api('labor', 'POST', {
                site: mySite?.name || '',
                contractor: f.get('contractor'),
                skilled: f.get('skilled'),
                unskilled: f.get('unskilled'),
            });
            const l = await api('labor');
            setLaborLogs(Array.isArray(l) ? l : []);
            closeM('labor');
            showToast('Labor attendance logged.');
        } catch { showToast('Failed to log labor. Please try again.'); }
    };

    const doResolve = async (id) => {
        try {
            await api(`issues/${id}`, 'PUT', { status: 'Resolved' });
            setIssues(p => p.map(x => x.id === id ? { ...x, status: 'Resolved' } : x));
            showToast('Issue marked as resolved.');
        } catch { showToast('Error resolving issue.'); }
    };

    const openTaskModal = (task) => { setSelectedTask(task); setRangeVal(task.progress || 0); openM('task'); };

    // ── Search filters ────────────────────────────────────────
    const sq = search.toLowerCase();
    const fTasks = allTasks.filter(t => t.name?.toLowerCase().includes(sq));
    const fInds = myIndents.filter(i =>
        i.project?.toLowerCase().includes(sq) ||
        ((i.items || [])[0]?.item || i.item || '').toLowerCase().includes(sq)
    );
    const fDprs = myDprs.filter(d => d.summary?.toLowerCase().includes(sq) || d.id?.toLowerCase().includes(sq));
    const fIssues = myIssues.filter(i => i.title?.toLowerCase().includes(sq));
    const fLabs = laborLogs.filter(l => l.site === mySite?.name);

    const ACTIONS = [
        { icon: FileSignature, label: 'Submit DPR', desc: 'Log daily site progress', c: '#10b981', bg: '#f0fdf4', onClick: () => openM('dpr') },
        { icon: Package, label: 'Raise Indent', desc: 'Request materials from store', c: '#f59e0b', bg: '#fffbeb', onClick: () => { setIndentCat(''); setIndentBrand(''); setIndentProduct(''); openM('indent'); } },
        { icon: HardHat, label: 'Log Labour', desc: 'Record today\'s labour by trade', c: '#1743e6', bg: '#eef3ff', onClick: () => setTab('Labour Attendance') },
        { icon: AlertOctagon, label: 'Report Issue', desc: 'Log quality or safety issue', c: '#ef4444', bg: '#fef2f2', onClick: () => openM('issue') },
        { icon: CalendarCheck, label: 'Update Task', desc: 'Update your task progress', c: '#8b5cf6', bg: '#f5f3ff', onClick: () => setTab('Tasks & Progress') },
        { icon: Camera, label: 'Site Gallery', desc: 'View site photo log', c: '#0ea5e9', bg: '#f0f9ff', onClick: () => setTab('Site Gallery') },
    ];

    const NAV = [
        { id: 'Field Dashboard', icon: LayoutDashboard },
        { id: 'Tasks & Progress', icon: CalendarCheck, alert: allTasks.filter(t => t.status === 'In Progress').length > 0 },
        { id: 'Material Indents', icon: Package, alert: pendingInds > 0 },
        { id: 'Daily Reports (DPR)', icon: FileSignature },
        { id: 'Labour Attendance', icon: HardHat },
        { id: 'Issues & Snags', icon: AlertOctagon, alert: openIssues > 0 },
        { id: 'Site Gallery', icon: ImageIcon2 },
        { id: 'My Expenses', icon: Receipt },
    ];

    // ── Error state ────────────────────────────────────────────
    if (!loading && error) {
        return (
            <>
                <style>{STYLES}</style>
                <div className="ef-root">
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 40 }}>
                        <AlertTriangle size={40} color="#ef4444" />
                        <div style={{ fontWeight: 700, fontSize: 16 }}>Failed to load data</div>
                        <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', maxWidth: 360 }}>{error}</div>
                        <button className="btn btn-primary" onClick={fetchAll}>
                            <RefreshCw size={14} /> Retry
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{STYLES}</style>
            <div className="ef-root">

                {/* SIDEBAR */}
                <aside className="ef-sb">
                    <div className="ef-logo">
                        <div className="ef-logo-icon"><Hammer size={15} color="#fff" /></div>
                        <div className="ef-logo-txt">Field<br />Portal</div>
                    </div>

                    {mySite && (
                        <div className="ef-pill">
                            <div className="lbl">Assigned Site</div>
                            <div className="nm">{mySite.name}</div>
                            <div className="loc"><MapPin size={9} />{mySite.location}</div>
                        </div>
                    )}

                    <nav className="ef-nav">
                        {NAV.map(n => (
                            <button key={n.id} className={`ef-ni ${tab === n.id ? 'active' : ''}`}
                                onClick={() => { setTab(n.id); setSearch(''); }}>
                                <n.icon size={13} />
                                {n.id}
                                {n.alert && <span className="dot" />}
                            </button>
                        ))}
                    </nav>

                    <div className="ef-user">
                        <div className="ef-av">{initials}</div>
                        <div>
                            <div className="ef-uname">{engineerName}</div>
                            <div className="ef-urole">{myEngineer?.role || 'Site Engineer'}</div>
                        </div>
                    </div>
                </aside>

                {/* MAIN */}
                <main className="ef-main">
                    <div className="ef-hdr">
                        <div>
                            <h1 className="ef-ttl">{tab}</h1>
                            <p className="ef-sub">M R Constructions · Field Operations</p>
                        </div>
                        <div className="ef-hr">
                            {tab !== 'Field Dashboard' && (
                                <div style={{ position: 'relative' }}>
                                    <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                                    <input
                                        placeholder="Search…" value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        style={{ padding: '8px 12px 8px 30px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none', width: '190px', fontFamily: 'Inter,sans-serif' }}
                                    />
                                </div>
                            )}
                            <button onClick={fetchAll} style={{ background: '#fff', border: '1px solid var(--border)', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', boxShadow: 'var(--sh)' }}>
                                <RefreshCw size={13} color="#94a3b8" className={loading ? 'spinning' : ''} />
                            </button>
                        </div>
                    </div>

                    {loading && !projects.length ? (
                        <div className="loading"><RefreshCw size={16} className="spinning" /> Loading site data…</div>
                    ) : (<>

                        {/* ── FIELD DASHBOARD ── */}
                        {tab === 'Field Dashboard' && (<>
                            {critItems.map((it, i) => (
                                <div key={i} className="abanner danger">
                                    <AlertOctagon size={14} />
                                    <strong>Stock Alert:</strong> {it.name} is {it.status} — raise an indent now
                                </div>
                            ))}
                            {openIssues > 0 && (
                                <div className="abanner warn">
                                    <AlertCircle size={14} />
                                    <strong>{openIssues} open issue{openIssues > 1 ? 's' : ''}</strong> assigned to you — tap Issues & Snags to review
                                </div>
                            )}

                            <div className="stat-strip sg">
                                <div className="sc" style={{ borderBottom: '3px solid #1743e6' }}>
                                    <div className="sc-lbl">Active Tasks</div>
                                    <div className="sc-val">{allTasks.filter(t => t.status === 'In Progress').length}</div>
                                    <div className="sc-sub">In progress</div>
                                </div>
                                <div className="sc" style={{ borderBottom: `3px solid ${openIssues > 0 ? '#ef4444' : '#10b981'}` }}>
                                    <div className="sc-lbl">Open Issues</div>
                                    <div className="sc-val" style={{ color: openIssues > 0 ? '#ef4444' : '#10b981' }}>{openIssues}</div>
                                    <div className="sc-sub">Assigned to me</div>
                                </div>
                                <div className="sc" style={{ borderBottom: '3px solid #f59e0b' }}>
                                    <div className="sc-lbl">My Indents</div>
                                    <div className="sc-val">{pendingInds}</div>
                                    <div className="sc-sub">Pending approval</div>
                                </div>
                                <div className="sc" style={{ borderBottom: '3px solid #10b981' }}>
                                    <div className="sc-lbl">My DPRs</div>
                                    <div className="sc-val">{myDprs.length}</div>
                                    <div className="sc-sub">Submitted</div>
                                </div>
                            </div>

                            {mySite && (
                                <div className="card" style={{ marginBottom: '18px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                        <div>
                                            <div style={{ fontSize: '14px', fontWeight: '800' }}>{mySite.name}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{mySite.client} · {mySite.location}</div>
                                        </div>
                                        <span className={`badge badge-${mySite.status?.toLowerCase().replace(/\s+/g, '-')}`}>{mySite.status}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                                        <span>Overall Progress</span>
                                        <strong style={{ color: 'var(--accent)' }}>{mySite.progress}%</strong>
                                    </div>
                                    <div className="pb"><div className="pf" style={{ width: `${mySite.progress}%`, background: 'var(--accent)' }} /></div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginTop: '14px' }}>
                                        {[
                                            ['Budget', `₹${mySite.budget}`, '#2f5bf0'],
                                            ['Spent', `₹${mySite.spent}`, '#ef4444'],
                                            ['Delay', `${mySite.delayed_days || 0}d`, (mySite.delayed_days || 0) > 0 ? '#ef4444' : '#10b981'],
                                        ].map(([l, v, c]) => (
                                            <div key={l} style={{ padding: '9px', background: '#f8fafc', borderRadius: '7px', border: '1px solid var(--border)' }}>
                                                <div style={{ fontSize: '9px', color: 'var(--muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px' }}>{l}</div>
                                                <div style={{ fontSize: '15px', fontWeight: '800', color: c }}>{v}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!mySite && !loading && (
                                <div className="card" style={{ marginBottom: '18px', textAlign: 'center', padding: '32px' }}>
                                    <MapPin size={32} color="#cbd5e1" style={{ marginBottom: 8 }} />
                                    <div style={{ fontWeight: 700, color: 'var(--muted)' }}>No site assigned yet</div>
                                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: 4 }}>Ask your admin to assign you to a project.</div>
                                </div>
                            )}

                            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>Quick Actions</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '10px' }} className="sg">
                                {ACTIONS.map((a, i) => (
                                    <div key={i} className="acard" onClick={a.onClick}>
                                        <div className="acard-ico" style={{ background: a.bg }}><a.icon size={20} color={a.c} /></div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '2px' }}>{a.label}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{a.desc}</div>
                                        </div>
                                        <ChevronRight size={13} color="#cbd5e1" style={{ flexShrink: 0 }} />
                                    </div>
                                ))}
                            </div>
                        </>)}

                        {/* ── TASKS ── */}
                        {tab === 'Tasks & Progress' && (
                            <TasksTree
                                projects={mySite ? [mySite] : []}
                                api={api}
                                showToast={showToast}
                                onReload={fetchAll}
                                initialProjectId={mySite?.id}
                                readOnlyStructure={true}
                                lockProject={true}
                            />
                        )}

                        {/* ── INDENTS ── */}
                        {tab === 'Material Indents' && (
                            <div className="card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '800' }}>My Material Requests</h3>
                                    <button className="btn btn-warn" onClick={() => { setIndentCat(''); setIndentBrand(''); setIndentProduct(''); openM('indent'); }}>
                                        <Plus size={12} /> Raise Indent
                                    </button>
                                </div>
                                <table className="tbl">
                                    <thead><tr><th>Indent ID</th><th>Material</th><th>Qty</th><th>Raised On</th><th>Priority</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {fInds.map(i => (
                                            <tr key={i.id}>
                                                <td style={{ fontWeight: '800', color: 'var(--accent)' }}>{i.id}</td>
                                                <td style={{ fontWeight: '600' }}>{(i.items || [])[0]?.item || i.item}</td>
                                                <td>{(i.items || [])[0]?.qty || i.qty} {(i.items || [])[0]?.uom || i.uom}</td>
                                                <td style={{ fontSize: '11px', color: 'var(--muted)' }}>{i.date}</td>
                                                <td><span style={{ fontSize: '10px', fontWeight: 'bold', color: i.priority === 'Urgent' ? '#ef4444' : i.priority === 'High' ? '#d97706' : 'var(--muted)' }}>{i.priority}</span></td>
                                                <td><span className={`badge badge-${i.status?.toLowerCase().replace(/\s+/g, '-')}`}>{i.status}</span></td>
                                            </tr>
                                        ))}
                                        {fInds.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: 'var(--muted)' }}>No indents raised yet. Use the button above to request materials.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── DPRS ── */}
                        {tab === 'Daily Reports (DPR)' && (
                            <div className="card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '800' }}>My Daily Reports</h3>
                                    <button className="btn btn-ok" onClick={() => openM('dpr')}>
                                        <Plus size={12} /> Submit DPR
                                    </button>
                                </div>
                                <table className="tbl">
                                    <thead><tr><th>DPR ID</th><th>Date</th><th>Labour Count</th><th>Summary</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {fDprs.map(d => (
                                            <tr key={d.id}>
                                                <td style={{ fontWeight: '800', color: 'var(--accent)' }}>{d.id}</td>
                                                <td style={{ fontWeight: '600' }}>{d.date}</td>
                                                <td style={{ fontWeight: '700' }}>{d.labor_count}</td>
                                                <td style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569' }}>{d.summary}</td>
                                                <td><span className={`badge badge-${d.status?.toLowerCase()}`}>{d.status}</span></td>
                                            </tr>
                                        ))}
                                        {fDprs.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: '32px', color: 'var(--muted)' }}>No DPRs submitted yet. Submit one at end of day.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── LABOUR ── */}
                        {tab === 'Labour Attendance' && (
                            <LabourVendorEntry projects={mySite ? [mySite] : projects} api={api} showToast={showToast}
                                user={{ full_name: engineerName, name: engineerName }} isSuper={false}
                                lockSite={true} fixedSite={mySiteName} />
                        )}

                        {/* ── ISSUES ── */}
                        {tab === 'Issues & Snags' && (
                            <div className="card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '800' }}>My Assigned Issues</h3>
                                    <button className="btn btn-danger" onClick={() => openM('issue')}>
                                        <AlertOctagon size={12} /> Report Issue
                                    </button>
                                </div>
                                <table className="tbl">
                                    <thead><tr><th>ID</th><th>Title</th><th>Severity</th><th>Date</th><th>Status</th><th>Action</th></tr></thead>
                                    <tbody>
                                        {fIssues.map(i => (
                                            <tr key={i.id}>
                                                <td style={{ fontWeight: '800', color: 'var(--accent)' }}>{i.id}</td>
                                                <td style={{ fontWeight: '600' }}>{i.title}</td>
                                                <td><span className={`badge badge-${i.severity?.toLowerCase()}`}>{i.severity}</span></td>
                                                <td style={{ fontSize: '11px', color: 'var(--muted)' }}>{i.date}</td>
                                                <td><span className={`badge badge-${i.status?.toLowerCase()}`}>{i.status}</span></td>
                                                <td>
                                                    {i.status === 'Open' && (
                                                        <button className="btn btn-ok" style={{ padding: '4px 9px', fontSize: '10px' }} onClick={() => doResolve(i.id)}>
                                                            <CheckCircle2 size={10} /> Resolve
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {fIssues.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: 'var(--muted)' }}>No issues assigned to you.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── MY EXPENSES ── */}
                        {tab === 'My Expenses' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {/* Summary strip */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
                                    {[
                                        ['Total Submitted', expenses.length, '#1743e6'],
                                        ['Pending Approval', expenses.filter(e => e.status === 'Pending').length, '#f59e0b'],
                                        ['Approved', expenses.filter(e => e.status === 'Approved').length, '#10b981'],
                                    ].map(([l, v, col]) => (
                                        <div key={l} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', borderTop: `3px solid ${col}` }}>
                                            <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{l}</div>
                                            <div style={{ fontSize: '24px', fontWeight: '800', color: col }}>{v}</div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>My Expense Claims</h3>
                                        <button className="btn btn-primary" onClick={() => setShowExpModal(true)}>
                                            <Receipt size={13} /> + Add Expense
                                        </button>
                                    </div>
                                    {expenses.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '13px' }}>
                                            No expenses submitted yet. Click Add Expense to submit your first claim.
                                        </div>
                                    ) : (
                                        <table className="tbl">
                                            <thead><tr><th>ID</th><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Status</th></tr></thead>
                                            <tbody>
                                                {expenses.filter(e => e.engineer === engineerName).map(exp => (
                                                    <tr key={exp.id}>
                                                        <td style={{ fontWeight: '700', color: '#f59e0b', fontSize: '11px' }}>{exp.id}</td>
                                                        <td style={{ fontSize: '11px', color: '#94a3b8' }}>{exp.date}</td>
                                                        <td><span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>{exp.category}</span></td>
                                                        <td style={{ fontSize: '12px', color: '#475569', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exp.description}</td>
                                                        <td style={{ fontWeight: '800', color: '#1743e6' }}>₹{parseFloat(exp.amount || 0).toLocaleString()}</td>
                                                        <td>
                                                            <span style={{
                                                                padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700',
                                                                background: exp.status === 'Approved' ? '#dcfce7' : exp.status === 'Rejected' ? '#fee2e2' : '#fef3c7',
                                                                color: exp.status === 'Approved' ? '#166534' : exp.status === 'Rejected' ? '#991b1b' : '#92400e'
                                                            }}>
                                                                {exp.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* My Invoices */}
                                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>My Invoices</h3>
                                        <button className="btn btn-primary" onClick={() => setShowInvModal(true)}>
                                            <Receipt size={13} /> + Add Invoice
                                        </button>
                                    </div>
                                    {invoices.filter(iv => iv.created_by === engineerName).length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '13px' }}>
                                            No invoices yet. Click Add Invoice to raise one.
                                        </div>
                                    ) : (
                                        <table className="tbl">
                                            <thead><tr><th>ID</th><th>Date</th><th>Vendor</th><th>Project</th><th>Total</th><th>Status</th></tr></thead>
                                            <tbody>
                                                {invoices.filter(iv => iv.created_by === engineerName).map(inv => (
                                                    <tr key={inv.id}>
                                                        <td style={{ fontWeight: '700', color: '#1743e6', fontSize: '11px' }}>{inv.id}</td>
                                                        <td style={{ fontSize: '11px', color: '#94a3b8' }}>{inv.invoice_date || ''}</td>
                                                        <td style={{ fontSize: '12px', color: '#475569' }}>{inv.vendor || '—'}</td>
                                                        <td style={{ fontSize: '12px', color: '#475569' }}>{inv.project || '—'}</td>
                                                        <td style={{ fontWeight: '800', color: '#1743e6' }}>₹{parseFloat(inv.total || inv.amount || 0).toLocaleString()}</td>
                                                        <td><span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', background: inv.status === 'Paid' ? '#dcfce7' : '#fef3c7', color: inv.status === 'Paid' ? '#166534' : '#92400e' }}>{inv.status || 'Unpaid'}</span></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        )}

                        {tab === 'Site Gallery' && (
                            <div className="card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '800' }}>Site Photo Log</h3>
                                </div>
                                {gallery.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)', fontSize: '13px' }}>
                                        <ImageIcon2 size={32} color="#cbd5e1" style={{ marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                                        No photos uploaded yet.
                                    </div>
                                ) : (
                                    <div className="gal-grid">
                                        {gallery
                                            .filter(g => !mySite || g.site === mySite.name)
                                            .map(g => (
                                                <div key={g.id} className="gal-card">
                                                    <img src={g.img} alt={g.desc} className="gal-img"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setLightbox(g)}
                                                        onError={e => { e.target.style.display = 'none'; }} />
                                                    <div style={{ padding: '10px' }}>
                                                        <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '2px' }}>{g.site}</div>
                                                        <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '5px' }}>{g.desc}</div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--muted)' }}>
                                                            <span>{g.uploader}</span>
                                                            <span>{g.date}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        )}

                    </>)}
                </main>
            </div>

            {/* ── TASK MODAL ── */}
            {modals.task && selectedTask && (
                <div className="moverlay">
                    <div className="mbox" style={{ maxWidth: '400px' }}>
                        <div className="mhdr">
                            <h2>Update Task Progress</h2>
                            <button className="mcls" onClick={() => closeM('task')}><X size={16} /></button>
                        </div>
                        <div className="mbody">
                            <form onSubmit={doUpdateTask}>
                                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '18px' }}>
                                    <div style={{ fontSize: '9px', color: 'var(--muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>Task</div>
                                    <div style={{ fontWeight: '700', fontSize: '14px' }}>{selectedTask.name}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{selectedTask.projectName}</div>
                                </div>
                                <div className="fg">
                                    <label>Progress (%)</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                                        <input type="range" min="0" max="100" value={rangeVal}
                                            onChange={e => setRangeVal(parseInt(e.target.value))}
                                            style={{ flex: 1, background: `linear-gradient(to right,#1743e6 ${rangeVal}%,#e2e8f0 ${rangeVal}%)` }} />
                                        <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: '#eef3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <span style={{ fontWeight: '800', fontSize: '15px', color: 'var(--accent)' }}>{rangeVal}%</span>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: '9px 12px', borderRadius: '7px', marginBottom: '16px', fontSize: '12px', fontWeight: '600', background: rangeVal === 100 ? '#f0fdf4' : rangeVal > 0 ? '#fffbeb' : '#f8fafc', border: '1px solid', borderColor: rangeVal === 100 ? '#bbf7d0' : rangeVal > 0 ? '#fde68a' : 'var(--border)', color: rangeVal === 100 ? '#16a34a' : rangeVal > 0 ? '#b45309' : 'var(--muted)' }}>
                                    Status will become: <strong>{rangeVal === 100 ? 'Completed' : rangeVal > 0 ? 'In Progress' : 'Planned'}</strong>
                                </div>
                                <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '11px' }}>Save Progress</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── INDENT MODAL ── */}
            {modals.indent && (
                <div className="moverlay">
                    <div className="mbox">
                        <div className="mhdr">
                            <h2>Raise Material Indent</h2>
                            <button className="mcls" onClick={() => closeM('indent')}><X size={16} /></button>
                        </div>
                        <div className="mbody">
                            <form onSubmit={doRaiseIndent}>
                                <div style={{ padding: '14px', border: '1px solid var(--border)', borderRadius: '9px', background: '#f8fafc', marginBottom: '12px' }}>
                                    <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <Box size={12} /> Material Catalog
                                    </div>
                                    <div className="fg">
                                        <label>Material Category</label>
                                        <select value={indentCat} onChange={e => { setIndentCat(e.target.value); setIndentBrand(''); }} required>
                                            <option value="" disabled>-- Select Category --</option>
                                            {MATERIAL_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                            <option value="Other">Other / Not Listed</option>
                                        </select>
                                    </div>
                                    {indentCat && indentCat !== 'Other' && (
                                        <div className="fg">
                                            <label>Brand / Manufacturer</label>
                                            <select value={indentBrand} onChange={e => setIndentBrand(e.target.value)} required>
                                                <option value="" disabled>-- Select Brand --</option>
                                                {(MATERIALS[indentCat] || []).map(b => <option key={b} value={b}>{b}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    {indentCat === 'Other' && (
                                        <div className="fg">
                                            <label>Describe the Material</label>
                                            <input name="custom_item" placeholder="e.g. Dr. Fixit Waterproofing Coat" required />
                                            <div style={{ fontSize: '10px', color: '#b45309', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                <AlertCircle size={10} /> Admin will be notified to add this to the catalog.
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid2" style={{ marginTop: '4px' }}>
                                        <div className="fg"><label>Quantity</label><input name="qty" type="number" min="1" placeholder="0" required /></div>
                                        <div className="fg">
                                            <label>Unit of Measure</label>
                                            <select name="uom" required>
                                                <option value="">-- UOM --</option>
                                                {['Bags', 'Tons', 'Trucks', 'm³', 'Units', 'Litres', 'Pieces', 'Metres'].map(u => <option key={u}>{u}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="fg">
                                    <label>Urgency / Priority</label>
                                    <select name="priority" required>
                                        <option value="Standard">Standard (3–5 Days)</option>
                                        <option value="High">High Priority (1–2 Days)</option>
                                        <option value="Urgent">Urgent (Today / Immediate)</option>
                                    </select>
                                </div>
                                <button type="submit" className="btn btn-warn" style={{ width: '100%', padding: '11px', marginTop: '4px' }}>Submit Indent to Admin</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── DPR MODAL ── */}
            {modals.dpr && (
                <div className="moverlay">
                    <div className="mbox">
                        <div className="mhdr">
                            <h2>Daily Progress Report</h2>
                            <button className="mcls" onClick={() => closeM('dpr')}><X size={16} /></button>
                        </div>
                        <div className="mbody">
                            <form onSubmit={doSubmitDPR}>
                                <div style={{ padding: '9px 12px', borderRadius: '7px', background: '#eef3ff', border: '1px solid #c9d7ff', fontSize: '12px', fontWeight: '600', color: 'var(--accent)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <MapPin size={12} /> {mySite?.name || 'No site assigned'}
                                </div>
                                <div className="fg">
                                    <label>Summary of Work Today</label>
                                    <textarea name="summary" placeholder="Describe today's activities, milestones achieved, and any blockers…" required />
                                </div>
                                <button type="submit" className="btn btn-ok" style={{ width: '100%', padding: '11px' }}>Submit DPR</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── ISSUE MODAL ── */}
            {modals.issue && (
                <div className="moverlay">
                    <div className="mbox" style={{ maxWidth: '440px' }}>
                        <div className="mhdr">
                            <h2>Report Quality / Safety Issue</h2>
                            <button className="mcls" onClick={() => closeM('issue')}><X size={16} /></button>
                        </div>
                        <div className="mbody">
                            <form onSubmit={doLogIssue}>
                                <div className="fg">
                                    <label>Issue Title</label>
                                    <input name="title" placeholder="e.g. Scaffolding loose at Block B, Level 3" required />
                                </div>
                                <div className="fg">
                                    <label>Severity</label>
                                    <select name="severity">
                                        <option value="Low">Low — minor observation</option>
                                        <option value="Medium">Medium — needs attention</option>
                                        <option value="High">High — urgent / safety risk</option>
                                    </select>
                                </div>
                                <button type="submit" className="btn btn-danger" style={{ width: '100%', padding: '11px', marginTop: '4px' }}>Report to Admin</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── LABOUR MODAL ── */}
            {false && modals.labor && (
                <div className="moverlay">
                    <div className="mbox" style={{ maxWidth: '440px' }}>
                        <div className="mhdr">
                            <h2>Log Labour Attendance</h2>
                            <button className="mcls" onClick={() => closeM('labor')}><X size={16} /></button>
                        </div>
                        <div className="mbody">
                            <form onSubmit={doLogLabor}>
                                <div className="fg">
                                    <label>Contractor / Agency</label>
                                    <input name="contractor" placeholder="e.g. Shree Labour Contractors" required />
                                </div>
                                <div className="grid2">
                                    <div className="fg"><label>Skilled Workers</label><input name="skilled" type="number" min="0" defaultValue="0" required /></div>
                                    <div className="fg"><label>Unskilled Workers</label><input name="unskilled" type="number" min="0" defaultValue="0" required /></div>
                                </div>
                                <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '11px' }}>Submit Muster Roll</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Expense Modal */}
            {showExpModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(6px)' }}>
                    <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9' }}>
                            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '800' }}>Submit Expense Claim</h2>
                            <button onClick={() => { setShowExpModal(false); setExpFile(null); setExpPickedUrl(''); }} style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '7px', display: 'flex' }}><X size={16} /></button>
                        </div>
                        <form style={{ padding: '20px 24px 24px' }} onSubmit={async (e) => {
                            e.preventDefault(); const f = new FormData(e.target);
                            const projName = f.get('project') || mySite?.name || '';
                            if (!expFile && !expPickedUrl) { showToast('Attach a receipt/bill, or pick one of your photos.'); return; }
                            try {
                                setExpUploading(true);
                                // Use a previously-uploaded photo if picked; otherwise upload the file now.
                                let receipt_url = expPickedUrl;
                                if (expFile) {
                                    const fd = new FormData();
                                    fd.append('file', expFile, expFile.name);
                                    fd.append('project', projName);
                                    fd.append('uploader', engineerName);
                                    const upRes = await fetch(`${API_BASE}/finance/expenses/upload`, {
                                        method: 'POST',
                                        headers: { ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) },
                                        body: fd,
                                    });
                                    if (!upRes.ok) {
                                        const er = await upRes.json().catch(() => ({}));
                                        throw new Error(er.error || 'Receipt upload failed');
                                    }
                                    receipt_url = (await upRes.json()).url || '';
                                }
                                await api('finance/expenses', 'POST', {
                                    engineer: engineerName,
                                    engineer_id: mySite?.id || 0,
                                    project: projName,
                                    category: f.get('category'),
                                    description: f.get('description'),
                                    amount: parseFloat(f.get('amount') || 0),
                                    date: f.get('date') || new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
                                    notes: f.get('notes'),
                                    receipt_url,
                                });
                                const exp = await api('finance/expenses');
                                setExpenses(Array.isArray(exp) ? exp : []);
                                setExpFile(null); setExpPickedUrl('');
                                setShowExpModal(false);
                                showToast('Expense submitted to Finance for approval.');
                            } catch (err) { showToast('Error: ' + (err.message || 'Failed')); }
                            finally { setExpUploading(false); }
                        }}>
                            <div className="fg" style={{ marginBottom: '13px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Category *</label>
                                <select name="category" required style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                    <option value="">-- Select --</option>
                                    <option>Travel</option>
                                    <option>Accommodation</option>
                                    <option>Food & Meals</option>
                                    <option>Fuel</option>
                                    <option>Tools & Equipment</option>
                                    <option>Site Materials</option>
                                    <option>Communication</option>
                                    <option>Medical</option>
                                    <option>Miscellaneous</option>
                                </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '13px' }}>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Amount (₹) *</label>
                                    <input name="amount" type="number" min="0" step="0.01" required placeholder="0.00"
                                        style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Date</label>
                                    <input name="date" type="date"
                                        style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                            </div>
                            <div className="fg" style={{ marginBottom: '13px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Project</label>
                                <select name="project" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                    <option value={mySite?.name || ''}>{mySite?.name || 'My Site'}</option>
                                    {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="fg" style={{ marginBottom: '13px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Description *</label>
                                <input name="description" required placeholder="e.g. Auto fare to site, lunch with client…"
                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                            </div>
                            <div className="fg" style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Additional Notes</label>
                                <textarea name="notes" rows={2} placeholder="Receipt details, bill number…"
                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif', resize: 'vertical' }} />
                            </div>
                            <div className="fg" style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Receipt / Bill * (PDF or image)</label>
                                {expPickedUrl ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', border: '1.5px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff' }}>
                                        <img src={expPickedUrl} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }} />
                                        <span style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>Using one of your uploaded photos</span>
                                        <button type="button" onClick={() => setExpPickedUrl('')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', padding: '2px' }}><X size={15} /></button>
                                    </div>
                                ) : !expFile ? (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '14px', border: '1.5px dashed #cbd5e1', borderRadius: '8px', fontSize: '13px', color: '#64748b', cursor: 'pointer', fontWeight: '600', background: '#f8fafc' }}>
                                        <Paperclip size={15} /> Attach from device
                                        <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                                            onChange={e => { setExpFile(e.target.files?.[0] || null); setExpPickedUrl(''); }} />
                                    </label>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1.5px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff' }}>
                                        <FileText size={16} color="#1743e6" />
                                        <span style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: '#1e40af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {expFile.name} <span style={{ color: '#64748b', fontWeight: '500' }}>({(expFile.size / 1024).toFixed(0)} KB)</span>
                                        </span>
                                        <button type="button" onClick={() => setExpFile(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', padding: '2px' }}><X size={15} /></button>
                                    </div>
                                )}
                                {!expFile && !expPickedUrl && myPhotos.length > 0 && (
                                    <div style={{ marginTop: '10px' }}>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '6px' }}>— or pick from your uploaded photos —</div>
                                        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                                            {myPhotos.map(g => (
                                                <img key={g.id} src={g.img || g.url} alt={g.desc} title={`${g.site || ''} · ${g.date || ''}`}
                                                    onClick={() => { setExpPickedUrl(g.img || g.url); setExpFile(null); }}
                                                    style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, border: '2px solid transparent' }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 13px', marginBottom: '14px', fontSize: '12px', color: '#92400e' }}>
                                💡 Your expense will be sent to the Finance team for approval. You'll be notified once approved.
                            </div>
                            <button type="submit" disabled={expUploading} style={{ width: '100%', padding: '12px', background: expUploading ? '#93a4e8' : '#1743e6', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: expUploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                <Receipt size={15} /> {expUploading ? 'Submitting…' : 'Submit for Approval'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Invoice Modal */}
            {showInvModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(6px)' }}>
                    <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9' }}>
                            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '800' }}>Raise Invoice</h2>
                            <button onClick={() => { setShowInvModal(false); setInvFile(null); setInvPickedUrl(''); }} style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '7px', display: 'flex' }}><X size={16} /></button>
                        </div>
                        <form style={{ padding: '20px 24px 24px' }} onSubmit={async (e) => {
                            e.preventDefault(); const f = new FormData(e.target);
                            const projName = f.get('project') || mySite?.name || '';
                            if (!invFile && !invPickedUrl) { showToast('Attach a bill, or pick one of your photos.'); return; }
                            try {
                                setInvUploading(true);
                                // Use a previously-uploaded photo if picked; otherwise upload the file now.
                                let file_url = invPickedUrl;
                                if (invFile) {
                                    const fd = new FormData();
                                    fd.append('file', invFile, invFile.name);
                                    fd.append('project', projName);
                                    fd.append('uploader', engineerName);
                                    const upRes = await fetch(`${API_BASE}/finance/invoices/upload`, {
                                        method: 'POST',
                                        headers: { ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) },
                                        body: fd,
                                    });
                                    if (!upRes.ok) {
                                        const er = await upRes.json().catch(() => ({}));
                                        throw new Error(er.error || 'File upload failed');
                                    }
                                    file_url = (await upRes.json()).url || '';
                                }
                                await api('finance/invoices', 'POST', {
                                    vendor: f.get('vendor'),
                                    project: projName,
                                    invoice_no: f.get('invoice_no'),
                                    invoice_date: f.get('invoice_date') || new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
                                    due_date: f.get('due_date'),
                                    amount: parseFloat(f.get('amount') || 0),
                                    tax: parseFloat(f.get('tax') || 0),
                                    category: f.get('category'),
                                    description: f.get('description'),
                                    notes: f.get('notes'),
                                    file_url,
                                    status: 'Pending',
                                });
                                setInvoices((await api('finance/invoices')) || []);
                                setInvFile(null); setInvPickedUrl('');
                                setShowInvModal(false);
                                showToast('Bill submitted to Finance for approval.');
                            } catch (err) { showToast('Error: ' + (err.message || 'Failed')); }
                            finally { setInvUploading(false); }
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '13px' }}>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Vendor *</label>
                                    <input name="vendor" placeholder="Supplier name (optional)" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Invoice No</label>
                                    <input name="invoice_no" placeholder="e.g. INV-2045" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                            </div>
                            <div className="fg" style={{ marginBottom: '13px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Project</label>
                                <select name="project" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                    <option value={mySite?.name || ''}>{mySite?.name || 'My Site'}</option>
                                    {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '13px' }}>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Amount (₹) *</label>
                                    <input name="amount" type="number" min="0" step="0.01" placeholder="0.00" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Tax / GST (₹)</label>
                                    <input name="tax" type="number" min="0" step="0.01" placeholder="0.00" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '13px' }}>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Invoice Date</label>
                                    <input name="invoice_date" type="date" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                                <div className="fg">
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Due Date</label>
                                    <input name="due_date" type="date" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                                </div>
                            </div>
                            <div className="fg" style={{ marginBottom: '13px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Category</label>
                                <select name="category" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                    <option>Material</option>
                                    <option>Labour</option>
                                    <option>Service</option>
                                    <option>Equipment</option>
                                    <option>Transport</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <div className="fg" style={{ marginBottom: '13px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Description</label>
                                <input name="description" placeholder="e.g. 50 bags cement — site delivery (optional)" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                            </div>
                            <div className="fg" style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Notes</label>
                                <textarea name="notes" rows={2} placeholder="Bill number, delivery ref…" style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif', resize: 'vertical' }} />
                            </div>
                            <div className="fg" style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Bill / Invoice File (PDF or image) *</label>
                                {invPickedUrl ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', border: '1.5px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff' }}>
                                        <img src={invPickedUrl} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }} />
                                        <span style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>Using one of your uploaded photos</span>
                                        <button type="button" onClick={() => setInvPickedUrl('')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', padding: '2px' }}><X size={15} /></button>
                                    </div>
                                ) : !invFile ? (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '14px', border: '1.5px dashed #f59e0b', borderRadius: '8px', fontSize: '13px', color: '#b45309', cursor: 'pointer', fontWeight: '600', background: '#fffbeb' }}>
                                        <Paperclip size={15} /> Attach from device
                                        <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                                            onChange={e => { setInvFile(e.target.files?.[0] || null); setInvPickedUrl(''); }} />
                                    </label>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1.5px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff' }}>
                                        <FileText size={16} color="#1743e6" />
                                        <span style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: '#1e40af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {invFile.name} <span style={{ color: '#64748b', fontWeight: '500' }}>({(invFile.size / 1024).toFixed(0)} KB)</span>
                                        </span>
                                        <button type="button" onClick={() => setInvFile(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', padding: '2px' }}><X size={15} /></button>
                                    </div>
                                )}
                                {!invFile && !invPickedUrl && myPhotos.length > 0 && (
                                    <div style={{ marginTop: '10px' }}>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '6px' }}>— or pick from your uploaded photos —</div>
                                        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                                            {myPhotos.map(g => (
                                                <img key={g.id} src={g.img || g.url} alt={g.desc} title={`${g.site || ''} · ${g.date || ''}`}
                                                    onClick={() => { setInvPickedUrl(g.img || g.url); setInvFile(null); }}
                                                    style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, border: '2px solid transparent' }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 13px', marginBottom: '14px', fontSize: '12px', color: '#1e40af' }}>
                                🧾 This bill goes to the Finance team for approval, tagged with your name.
                            </div>
                            <button type="submit" disabled={invUploading || !invFile} style={{ width: '100%', padding: '12px', background: (invUploading || !invFile) ? '#93a4e8' : '#1743e6', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: (invUploading || !invFile) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                <Receipt size={15} /> {invUploading ? 'Submitting…' : 'Submit for Approval'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* TOAST */}
            {lightbox && (
                <div onClick={() => setLightbox(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9998, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', backdropFilter: 'blur(4px)' }}>
                    <img src={lightbox.img} alt={lightbox.desc}
                        style={{ maxWidth: '92vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
                        onClick={e => e.stopPropagation()} />
                    <div style={{ marginTop: '14px', textAlign: 'center', color: '#fff' }} onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize: '13px', fontWeight: '700' }}>{lightbox.site}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px', wordBreak: 'break-all' }}>{lightbox.desc}</div>
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '3px' }}>{lightbox.uploader} · {lightbox.date}</div>
                    </div>
                    <button onClick={() => setLightbox(null)}
                        style={{ position: 'fixed', top: '16px', right: '20px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', color: '#fff', fontSize: '20px', zIndex: 9999 }}>
                        ✕
                    </button>
                </div>
            )}

            {toast && (
                <div style={{ position: 'fixed', bottom: '20px', right: '20px', background: '#0f172a', color: '#fff', padding: '11px 18px', borderRadius: '10px', boxShadow: 'var(--shlg)', display: 'flex', alignItems: 'center', gap: '9px', zIndex: 9999, fontWeight: '500', fontSize: '13px', borderLeft: '3px solid #10b981', animation: 'slideUp 0.2s', fontFamily: 'Inter,sans-serif', maxWidth: '360px' }}>
                    <CheckCircle2 size={14} color="#10b981" />
                    {toast}
                </div>
            )}
        </>
    );
}